import { PrivilegeType, TeamRole } from '@/consts/team'
import { TOKEN_CACHE_DURATION } from '@/consts/upload'
import { useTeamStore } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import http, { createInstance, RequestConf } from '@/utils/http'

import { FSContext } from './types'

/**
 * Platform FS Context
 * Used for personal datadrive access (Platform users)
 */
export class PlatformFSContext implements FSContext {
  private static readonly PRIVILEGE_TYPE = 17 // PERSONAL_BOX
  private tokenCache: {
    token: string
    expiredAt: number
    domain: number
  } = {
    token: '',
    expiredAt: 0,
    domain: -1,
  }

  private apiInstance: any

  constructor() {
    // Create API instance with interceptor for token injection
    this.apiInstance = createInstance('', 'fs')
    this.apiInstance.interceptors.request.use(async (config: RequestConf) => {
      config.withCredentials = true
      config.domain ??= 0

      config.baseURL = useUserStore.getState().domainCurrentServiceMap[config.domain]

      if (!config.baseURL) {
        throw {
          code: -7,
          message: 'target service not found',
        }
      }

      const token = await this.getToken(config.domain)
      if (token) {
        config.headers = {
          ...(config.headers || {}),
          Authorization: token,
        }
      }

      return config
    })
  }

  async getToken(domain: number): Promise<string> {
    // Check cache validity (60 seconds)
    if (this.tokenCache.expiredAt < Date.now() || domain !== this.tokenCache.domain) {
      const token = await this.refreshToken(domain)
      Object.assign(this.tokenCache, {
        token: `Bearer ${token}`,
        expiredAt: Date.now() + TOKEN_CACHE_DURATION,
        domain,
      })
    }

    return this.tokenCache.token
  }

  private async refreshToken(domain: number): Promise<string> {
    const { code, token } = await http.post('/auth/grant', {
      privilege_type: PlatformFSContext.PRIVILEGE_TYPE,
      source_id: null,
      domain: domain,
    })

    if (code === 0) {
      return token as string
    }

    throw new Error('Failed to refresh platform FS token')
  }

  getApiInstance(): any {
    return this.apiInstance
  }

  getBaseURL(domain: number): string | undefined {
    return useUserStore.getState().domainCurrentServiceMap[domain]
  }
}

/**
 * Team FS Context
 * Used for team shared datadrive access
 */
export class TeamFSContext implements FSContext {
  private tokenCaches = new Map<
    number,
    {
      token: string
      expiredAt: number
      domain: number
    }
  >()

  private apiInstance: any
  private userRole: TeamRole
  private teamId: number

  constructor(userRole: TeamRole, teamId: number) {
    this.userRole = userRole
    this.teamId = teamId

    // Create API instance (no interceptor - tokens are manually injected)
    this.apiInstance = createInstance('', 'fs')
    this.apiInstance.interceptors.request.use(async (config: RequestConf) => {
      config.withCredentials = true
      config.domain ??= 0

      // Priority 1: Platform user's service map
      // Priority 2: Team-specific service map
      config.baseURL =
        useUserStore.getState().domainCurrentServiceMap[config.domain] ||
        useTeamStore.getState().teamDomainServiceMap?.[config.domain]

      if (!config.baseURL) {
        throw {
          code: -7,
          message: `No service URL found for domain ${config.domain}. Please ensure team info is loaded.`,
        }
      }

      return config
    })
  }

  async getToken(domain: number): Promise<string> {
    // Determine privilege type based on role
    const privilegeType =
      this.userRole === TeamRole.Owner || this.userRole === TeamRole.Admin
        ? PrivilegeType.TeamBoxReadWrite // 19 - full access
        : PrivilegeType.TeamBoxReadOnly // 18 - read-only

    // Check cache (60 seconds validity)
    const cacheKey = privilegeType
    const cache = this.tokenCaches.get(cacheKey)
    if (cache && cache.expiredAt > Date.now() && cache.domain === domain) {
      return cache.token
    }

    // Call /auth/grant to get new token
    const { code, token } = await http.post('/auth/grant', {
      privilege_type: privilegeType,
      source_id: this.teamId,
      domain: domain,
    })

    if (code === 0) {
      const bearerToken = `Bearer ${token}`
      this.tokenCaches.set(cacheKey, {
        token: bearerToken,
        expiredAt: Date.now() + TOKEN_CACHE_DURATION,
        domain,
      })
      return bearerToken
    }

    throw new Error('Failed to get team FS token')
  }

  getApiInstance(): any {
    return this.apiInstance
  }

  getBaseURL(domain: number): string | undefined {
    // Priority 1: Platform user's service map
    const platformMap = useUserStore.getState().domainCurrentServiceMap[domain]
    if (platformMap) return platformMap

    // Priority 2: Team-specific service map
    return useTeamStore.getState().teamDomainServiceMap?.[domain]
  }
}
